import { supabase } from '@/integrations/supabase/client';

export type ConsentType = 
  | 'cookie_essential'
  | 'cookie_analytics'
  | 'cookie_marketing'
  | 'cookie_visitor_id'
  | 'tcpa_marketing'
  | 'tcpa_sms'
  | 'do_not_sell'
  | 'email_marketing'
  | 'dsr_access'
  | 'dsr_deletion'
  | 'dsr_portability'
  | 'gpc_signal_detected';

interface LogConsentOptions {
  type: ConsentType;
  granted: boolean;
  userIdentifier?: string;
}

/**
 * Log a consent event via secure edge function
 */
export const logConsent = async ({ type, granted, userIdentifier }: LogConsentOptions): Promise<void> => {
  try {
    const identifier = userIdentifier || localStorage.getItem('anonymous_id') || 'unknown';
    
    // Use edge function instead of direct database insert for security
    await supabase.functions.invoke('log-consent', {
      body: {
        user_identifier: identifier,
        consent_type: type,
        consent_granted: granted,
        user_agent: navigator.userAgent,
        page_url: window.location.href,
      },
    });
  } catch (error) {
    console.error('Failed to log consent:', error);
    // Don't throw - consent logging failures shouldn't break the app
  }
};

/**
 * Log multiple consent events at once via secure edge function
 */
export const logMultipleConsents = async (
  consents: Array<{ type: ConsentType; granted: boolean }>,
  userIdentifier?: string
): Promise<void> => {
  const identifier = userIdentifier || localStorage.getItem('anonymous_id') || 'unknown';
  
  try {
    const records = consents.map(({ type, granted }) => ({
      user_identifier: identifier,
      consent_type: type,
      consent_granted: granted,
      user_agent: navigator.userAgent,
      page_url: window.location.href,
    }));

    // Use edge function for batch insert
    await supabase.functions.invoke('log-consent', {
      body: records,
    });
  } catch (error) {
    console.error('Failed to log multiple consents:', error);
  }
};

/**
 * Log TCPA consent (for phone/SMS marketing)
 */
export const logTCPAConsent = async (granted: boolean, phone?: string): Promise<void> => {
  await logConsent({
    type: 'tcpa_marketing',
    granted,
    userIdentifier: phone,
  });
};

/**
 * Check if visitor ID consent was given
 */
export const hasVisitorIdConsent = (): boolean => {
  try {
    const saved = localStorage.getItem('cookie_consent_preferences');
    if (!saved) return false;
    const prefs = JSON.parse(saved);
    return prefs.visitorId === true;
  } catch {
    return false;
  }
};

/**
 * Check if analytics consent was given
 */
export const hasAnalyticsConsent = (): boolean => {
  try {
    const saved = localStorage.getItem('cookie_consent_preferences');
    if (!saved) return false;
    const prefs = JSON.parse(saved);
    return prefs.analytics === true;
  } catch {
    return false;
  }
};

/**
 * Check if marketing consent was given
 */
export const hasMarketingConsent = (): boolean => {
  try {
    const saved = localStorage.getItem('cookie_consent_preferences');
    if (!saved) return false;
    const prefs = JSON.parse(saved);
    return prefs.marketing === true;
  } catch {
    return false;
  }
};
